import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Gauge, ClipboardText, Warning, CheckCircle, Users, ChartBar } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import SlaBeacon from "../components/SlaBeacon";

const STATUS_LABELS = {
  draft: "Draft", awaiting_quotes: "Awaiting Quotes", quotes_received: "Quotes Received",
  submitted_to_client: "Quote with Client", client_approved: "Client Approved",
  converting: "Converting", converted: "Converted", declined: "Declined", on_hold: "On Hold",
};

function SlaCell({ alert, level, onClear }) {
  if (!alert) return <span className="text-xs text-emerald-600 dark:text-emerald-400">—</span>;
  const active = !alert.resolved_at;
  const ackNames = (alert.acknowledgments || []).map(a => a.name).filter(Boolean);
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1.5">
        {active && <SlaBeacon level={level} />}
        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${level === "red" ? "bg-red-600 text-white" : "bg-amber-500 text-white"} ${!active ? "opacity-40" : ""}`}>
          {active ? "Missed" : "Cleared"}
        </span>
        <span className="text-[10px] text-muted-foreground">{new Date(alert.triggered_at).toLocaleDateString("en-GB")}</span>
      </div>
      <span className="text-[10px] text-muted-foreground flex items-center gap-1" title={ackNames.join(", ") || "No acknowledgments yet"}>
        <Users size={10} /> {ackNames.length} ack{ackNames.length !== 1 ? "s" : ""}
        {alert.resolved_at && <span className="text-emerald-600 dark:text-emerald-400"> · cleared by {alert.resolved_by_name}</span>}
      </span>
      {active && (
        <Button variant="outline" size="sm" className="h-6 text-[10px] px-2 w-fit" onClick={() => onClear(alert.id)} data-testid={`sla-clear-${level}-${alert.jr_id}`}>
          Clear beacon
        </Button>
      )}
    </div>
  );
}

export default function QuoteSlaPage() {
  const [data, setData] = useState(null);
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showClosed, setShowClosed] = useState(false);

  const fetchData = useCallback(() => {
    API.get("/quote-sla/overview")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Failed to load SLA data"))
      .finally(() => setLoading(false));
    API.get("/quote-sla/trends").then(({ data }) => setTrends(data.weeks || [])).catch(() => {});
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const clearBeacon = async (alertId) => {
    try {
      await API.post(`/quote-sla/alerts/${alertId}/resolve`);
      toast.success("Beacon cleared");
      fetchData();
    } catch { toast.error("Failed to clear beacon"); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;

  const rows = (data?.rows || []).filter(r => showClosed || r.is_open);
  const stats = data?.stats || {};

  return (
    <div data-testid="quote-sla-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-[Chivo] tracking-tight flex items-center gap-2"><Gauge size={28} /> Quote SLAs</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Amber = {data?.rules?.amber_days} working days without a quote · Red = {data?.rules?.red_days} working days without a quote sent to the client
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setShowClosed(s => !s)} data-testid="sla-toggle-closed">
          {showClosed ? "Hide closed requests" : "Show closed requests"}
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="border border-border rounded-md p-4 bg-card" data-testid="sla-stat-open">
          <div className="flex items-center gap-2 text-muted-foreground"><ClipboardText size={14} /><span className="text-[10px] uppercase tracking-wider font-semibold">Awaiting quotes</span></div>
          <p className="text-2xl font-bold mt-1">{stats.open ?? 0}</p>
        </div>
        <div className="border border-amber-300 dark:border-amber-800 rounded-md p-4 bg-amber-50 dark:bg-amber-950/30" data-testid="sla-stat-amber">
          <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400"><Warning size={14} /><span className="text-[10px] uppercase tracking-wider font-semibold">Amber breached</span></div>
          <p className="text-2xl font-bold mt-1 text-amber-700 dark:text-amber-400">{stats.amber_active ?? 0}</p>
        </div>
        <div className="border border-red-300 dark:border-red-800 rounded-md p-4 bg-red-50 dark:bg-red-950/30" data-testid="sla-stat-red">
          <div className="flex items-center gap-2 text-red-700 dark:text-red-400"><Warning size={14} weight="fill" /><span className="text-[10px] uppercase tracking-wider font-semibold">Red breached</span></div>
          <p className="text-2xl font-bold mt-1 text-red-700 dark:text-red-400">{stats.red_active ?? 0}</p>
        </div>
      </div>

      {trends.length > 0 && (
        <div className="border border-border rounded-md bg-card p-4 mb-6" data-testid="sla-trends-chart">
          <h3 className="text-sm font-semibold flex items-center gap-1.5 mb-3"><ChartBar size={15} /> Weekly SLA breaches — last 12 weeks</h3>
          <ResponsiveContainer width="100%" height={170}>
            <BarChart data={trends} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
              <XAxis dataKey="week" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
              <Tooltip cursor={{ fill: "rgba(100,116,139,0.08)" }}
                contentStyle={{ fontSize: 12, borderRadius: 6 }}
                formatter={(v, name) => [v, name === "amber" ? "Amber breaches" : "Red breaches"]} />
              <Bar dataKey="amber" name="amber" fill="#f59e0b" radius={[3, 3, 0, 0]} maxBarSize={22} />
              <Bar dataKey="red" name="red" fill="#dc2626" radius={[3, 3, 0, 0]} maxBarSize={22} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="border border-border rounded-md bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40">
              <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Request</th>
              <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Client</th>
              <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Status</th>
              <th className="text-center px-4 py-2.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Quotes</th>
              <th className="text-center px-4 py-2.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Working days on system</th>
              <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">2-day SLA (no quote)</th>
              <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">4-day SLA (not sent to client)</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-muted-foreground">
                <CheckCircle size={24} className="mx-auto mb-2 text-emerald-500" /> No job requests to show
              </td></tr>
            )}
            {rows.map(r => {
              const dayColour = (r.red && !r.red.resolved_at) ? "text-red-600 dark:text-red-400"
                : (r.amber && !r.amber.resolved_at) ? "text-amber-600 dark:text-amber-400" : "";
              return (
                <tr key={r.id} className={`border-b border-border last:border-0 row-hover ${!r.is_open ? "opacity-50" : ""}`} data-testid={`sla-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <Link to={`/job-requests/${r.id}`} className="hover:underline">
                      <span className="text-xs font-mono text-muted-foreground block">{r.reference}</span>
                      <span className="font-semibold text-sm">{r.title}</span>
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{r.client_name || "—"}</td>
                  <td className="px-4 py-3"><Badge variant="outline" className="text-[10px]">{STATUS_LABELS[r.status] || r.status}</Badge></td>
                  <td className="px-4 py-3 text-center text-sm">{r.quotes_count}</td>
                  <td className={`px-4 py-3 text-center text-lg font-bold ${dayColour}`} data-testid={`sla-days-${r.id}`}>{r.working_days_open}</td>
                  <td className="px-4 py-3"><SlaCell alert={r.amber} level="amber" onClear={clearBeacon} /></td>
                  <td className="px-4 py-3"><SlaCell alert={r.red} level="red" onClear={clearBeacon} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-muted-foreground mt-3">
        Breach alerts email the office automatically and every admin must acknowledge them on the dashboard. Clearing a beacon stops it flashing on the Requests list — the alert history stays here.
      </p>
    </div>
  );
}
